import React, { useEffect, useState } from "react";
import ResourceEditor from "./ResourceEditor";
import api from "../lib/api";
import { Calendar } from "lucide-react";

const DAYS = [
  { value: "monday", label: "Lundi" },
  { value: "tuesday", label: "Mardi" },
  { value: "wednesday", label: "Mercredi" },
  { value: "thursday", label: "Jeudi" },
  { value: "friday", label: "Vendredi" },
  { value: "saturday", label: "Samedi" },
  { value: "sunday", label: "Dimanche" },
];

const DAY_LABEL = Object.fromEntries(DAYS.map((d) => [d.value, d.label]));

const minutesBetween = (start, end) => {
  try {
    const [sh, sm] = (start || "").split(":").map(Number);
    const [eh, em] = (end || "").split(":").map(Number);
    const diff = (eh * 60 + em) - (sh * 60 + sm);
    return Math.max(0, diff);
  } catch { return 0; }
};

const fmtDuration = (mins) => {
  if (!mins) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h${String(m).padStart(2, "0")}`;
  if (h) return `${h}h`;
  return `${m} min`;
};

const ScheduleAdmin = () => {
  const [teachers, setTeachers] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/admin/teachers").then(({ data }) => setTeachers(data || [])).catch(() => {});
    api.get("/admin/categories").then(({ data }) => setCategories(data || [])).catch(() => {});
  }, []);

  const teacherOptions = teachers.map((t) => ({ value: t.id, label: t.name }));
  const categoryOptions = categories.map((c) => ({ value: c.name, label: c.name }));

  return (
    <ResourceEditor
      resource="schedule_slots"
      title="Planning des créneaux"
      subtitle="Chaque créneau hebdomadaire de la saison. Le tarif annuel est saisi directement sur le créneau — le total à payer sera la somme des créneaux choisis (avec réductions multi-cours)."
      groupByField="day"
      groupByLabel={(d) => DAY_LABEL[d] || d}
      fields={[
        { key: "day", label: "Jour", type: "select", options: DAYS },
        { key: "title", label: "Libellé du créneau", placeholder: "Ex: Préparatoire 3" },
        { key: "startTime", label: "Heure de début (HH:MM)", placeholder: "17:15" },
        { key: "endTime", label: "Heure de fin (HH:MM)", placeholder: "18:30" },
        { key: "price", label: "Tarif annuel (€) *", type: "number", placeholder: "Ex: 360" },
        { key: "category", label: "Catégorie", type: "select", options: categoryOptions },
        { key: "teacherId", label: "Professeur", type: "select", options: teacherOptions },
        { key: "room", label: "Salle", placeholder: "Salle de danse" },
        { key: "maxStudents", label: "Capacité max (0 = illimitée)", type: "number", placeholder: "0" },
      ]}
      emptyItem={{ day: "tuesday", title: "", startTime: "", endTime: "", price: 0, category: "", teacherId: "", room: "Salle de danse", maxStudents: 0 }}
      renderPreview={(s) => {
        const teacher = teachers.find((t) => t.id === s.teacherId);
        const minutes = minutesBetween(s.startTime, s.endTime);
        return (
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-deep-rose">
                <Calendar size={12} /> {DAY_LABEL[s.day] || s.day}
              </span>
              <span className="text-stone-700 text-sm font-medium">{s.startTime} - {s.endTime}</span>
              <span className="text-xs text-stone-400">({fmtDuration(minutes)})</span>
              {s.price > 0 && <span className="text-xs text-emerald-700 font-medium">· {Number(s.price).toFixed(0)} €/an</span>}
            </div>
            <div className="font-serif-display text-stone-800 mt-0.5 truncate">{s.title || "(sans titre)"}</div>
            <div className="text-stone-500 text-xs">
              {s.category && <span className="mr-2">{s.category}</span>}
              {teacher?.name && <span className="mr-2">· {teacher.name}</span>}
              {s.room && <span>· {s.room}</span>}
              {s.maxStudents > 0 && <span className="ml-2 text-amber-700">· max {s.maxStudents}</span>}
            </div>
          </div>
        );
      }}
    />
  );
};

export default ScheduleAdmin;
